<template>
  <div class="container-fluid mt-4" :class="themeClass">

    <!-- Cargando -->
    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <p class="mt-2 text-muted">Cargando sesión...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
    </div>

    <div v-else-if="sesion">

      <!-- Encabezado de sesión -->
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h5 class="mb-0">
            <i class="bi bi-cash-register me-2"></i>
            {{ sesion.nombre_caja }}
            <span class="ms-2" :class="sesion.estado === 'abierta' ? 'badge bg-success' : 'badge bg-secondary'">
              {{ sesion.estado }}
            </span>
          </h5>
          <small class="text-muted">
            Apertura: {{ formatearFecha(sesion.fecha_apertura) }}
            <span v-if="sesion.fecha_cierre"> · Cierre: {{ formatearFecha(sesion.fecha_cierre) }}</span>
            <span v-if="sesion.nombre_usuario"> · Operador: {{ sesion.nombre_usuario }}</span>
          </small>
        </div>
        <router-link to="/cajas/historial" class="btn btn-outline-secondary btn-sm">
          <i class="bi bi-arrow-left me-1"></i> Volver al historial
        </router-link>
      </div>

      <!-- Resumen de la sesión -->
      <div class="row g-3 mb-4">
        <div class="col-6 col-md-2">
          <div class="card text-center border-0 shadow-sm h-100">
            <div class="card-body py-2">
              <div class="text-muted small">Apertura</div>
              <div class="fw-bold">${{ fmt(sesion.saldo_apertura) }}</div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="card text-center border-0 shadow-sm h-100">
            <div class="card-body py-2">
              <div class="text-muted small">Ingresos</div>
              <div class="fw-bold text-success">+${{ fmt(resumen.total_ingresos) }}</div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="card text-center border-0 shadow-sm h-100">
            <div class="card-body py-2">
              <div class="text-muted small">Egresos</div>
              <div class="fw-bold text-danger">-${{ fmt(resumen.total_egresos) }}</div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-2">
          <div class="card text-center border-0 shadow-sm bg-light h-100">
            <div class="card-body py-2">
              <div class="text-muted small">Saldo</div>
              <div class="fw-bold text-primary">${{ fmt(parseFloat(sesion.saldo_apertura) + resumen.saldo_actual) }}</div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-2" v-if="sesion.estado === 'cerrada'">
          <div class="card text-center border-0 shadow-sm h-100">
            <div class="card-body py-2">
              <div class="text-muted small">Conteo real</div>
              <div class="fw-bold">${{ fmt(sesion.saldo_real) }}</div>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-2" v-if="sesion.estado === 'cerrada'">
          <div class="card text-center border-0 shadow-sm h-100">
            <div class="card-body py-2">
              <div class="text-muted small">Diferencia</div>
              <div class="fw-bold" :class="parseFloat(sesion.diferencia) >= 0 ? 'text-success' : 'text-danger'">
                {{ parseFloat(sesion.diferencia) >= 0 ? '+' : '' }}${{ fmt(sesion.diferencia) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Créditos pendientes (si existe) -->
      <div v-if="resumen.total_creditos > 0" class="alert alert-warning mb-4 d-flex justify-content-between align-items-center">
        <span>
          <i class="bi bi-exclamation-triangle me-2"></i>
          <strong>{{ resumen.total_creditos }}</strong> crédito(s) pendiente(s) · Saldo: <strong>${{ fmt(resumen.saldo_creditos) }}</strong>
        </span>
        <router-link to="/cajas/creditos" class="btn btn-warning btn-sm">
          Ver créditos
        </router-link>
      </div>

      <!-- Tabla de movimientos -->
      <div class="card shadow-sm">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0"><i class="bi bi-list-ul me-2"></i>Movimientos ({{ movimientos.length }})</h6>
          <div class="d-flex gap-2">
            <select v-model="filtroTipo" class="form-select form-select-sm" style="width:120px">
              <option value="">Todos</option>
              <option value="ingreso">Ingresos</option>
              <option value="egreso">Egresos</option>
            </select>
            <select v-model="filtroOrigen" class="form-select form-select-sm" style="width:140px">
              <option value="">Todos los orígenes</option>
              <option value="factura">Factura</option>
              <option value="pago">Pago</option>
              <option value="ajuste">Ajuste</option>
              <option value="transferencia">Transferencia</option>
              <option value="credito">Crédito</option>
            </select>
          </div>
        </div>
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0">
            <thead class="table-light">
              <tr>
                <th>#</th>
                <th>Tipo</th>
                <th>Origen</th>
                <th>Concepto / Descripción</th>
                <th>N° Comprobante</th>
                <th class="text-end">Monto</th>
                <th>Operador</th>
                <th>Fecha/Hora</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="movimientosFiltrados.length === 0">
                <td colspan="9" class="text-center py-4 text-muted">
                  <i class="bi bi-inbox me-2"></i>
                  Sin movimientos {{ filtroTipo ? 'de tipo "' + filtroTipo + '"' : '' }}
                </td>
              </tr>
              <tr v-for="mov in movimientosFiltrados" :key="mov.id">
                <td class="text-muted small">{{ mov.id }}</td>
                <td>
                  <span :class="mov.tipo === 'ingreso' ? 'badge bg-success' : 'badge bg-danger'">
                    {{ mov.tipo }}
                  </span>
                </td>
                <td><span class="badge bg-secondary">{{ mov.origen }}</span></td>
                <td>{{ mov.concepto || mov.descripcion || '—' }}</td>
                <td class="small text-muted">{{ mov.numero_comprobante || '—' }}</td>
                <td
                  class="text-end fw-semibold"
                  :class="mov.tipo === 'ingreso' ? 'text-success' : 'text-danger'"
                >
                  {{ mov.tipo === 'ingreso' ? '+' : '-' }}${{ fmt(mov.monto) }}
                </td>
                <td class="small">{{ mov.nombre_usuario || '—' }}</td>
                <td class="small">{{ formatearFecha(mov.created_at) }}</td>
                <td class="text-end" style="white-space:nowrap">
                  <template v-if="esManual(mov)">
                    <button v-if="puedeEditar" class="btn btn-outline-secondary btn-sm me-1" title="Editar" @click="abrirEditar(mov)">
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button v-if="puedeEliminar" class="btn btn-outline-danger btn-sm" title="Eliminar" @click="eliminar(mov)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </template>
                </td>
              </tr>
            </tbody>
            <tfoot class="table-light fw-semibold" v-if="movimientosFiltrados.length > 0">
              <tr>
                <td colspan="5" class="text-end">Subtotales filtro:</td>
                <td class="text-end">
                  <span class="text-success">+${{ fmt(subtotalIngresos) }}</span>
                  /
                  <span class="text-danger">-${{ fmt(subtotalEgresos) }}</span>
                </td>
                <td colspan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

    </div>
  </div>

  <!-- Modal edición de movimiento manual -->
  <div v-if="modalEditar.visible" class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.5)">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title"><i class="bi bi-pencil me-2"></i>Editar movimiento #{{ modalEditar.id }}</h5>
          <button type="button" class="btn-close" @click="modalEditar.visible = false"></button>
        </div>
        <div class="modal-body">
          <div v-if="modalEditar.error" class="alert alert-danger py-2">{{ modalEditar.error }}</div>
          <div class="mb-3">
            <label class="form-label fw-semibold">Monto</label>
            <div class="input-group">
              <span class="input-group-text">$</span>
              <input v-model="modalEditar.monto" type="number" step="0.01" min="0.01" class="form-control" />
            </div>
          </div>
          <div class="mb-3">
            <label class="form-label fw-semibold">Descripción</label>
            <input v-model="modalEditar.descripcion" type="text" class="form-control" maxlength="255" />
          </div>
          <div v-if="modalEditar.esTransferencia" class="alert alert-info py-2 small">
            <i class="bi bi-info-circle me-1"></i> Este depósito también actualizará el movimiento de banco asociado.
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="modalEditar.visible = false">Cancelar</button>
          <button class="btn btn-primary" :disabled="modalEditar.guardando" @click="guardarEdicion">
            <span v-if="modalEditar.guardando" class="spinner-border spinner-border-sm me-1"></span>
            Guardar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import themeManager from '@/store/theme'

export default {
  name: 'ClientCajasMovimientos',

  data() {
    return {
      cargando:      true,
      error:         null,
      sesion:        null,
      movimientos:   [],
      resumen:       { total_ingresos: 0, total_egresos: 0, saldo_actual: 0, total_creditos: 0, saldo_creditos: 0 },
      filtroTipo:    '',
      filtroOrigen:  '',
      modalEditar: {
        visible: false, guardando: false, error: null,
        id: null, monto: 0, descripcion: '', esTransferencia: false,
      },
    }
  },

  computed: {
    themeClass() { return themeManager.themeClass },
    puedeEditar() {
      return this.$store.getters.puedoHacer('/cajas', 'editar')
    },
    puedeEliminar() {
      return this.$store.getters.puedoHacer('/cajas', 'eliminar')
    },

    movimientosFiltrados() {
      return this.movimientos.filter(m => {
        if (this.filtroTipo   && m.tipo   !== this.filtroTipo)   return false
        if (this.filtroOrigen && m.origen !== this.filtroOrigen) return false
        return true
      })
    },

    subtotalIngresos() {
      return this.movimientosFiltrados
        .filter(m => m.tipo === 'ingreso')
        .reduce((s, m) => s + parseFloat(m.monto || 0), 0)
    },

    subtotalEgresos() {
      return this.movimientosFiltrados
        .filter(m => m.tipo === 'egreso')
        .reduce((s, m) => s + parseFloat(m.monto || 0), 0)
    },
  },

  async created() {
    await this.cargar()
  },

  methods: {
    async cargar() {
      this.cargando = true
      const sesionId = this.$route.params.sesion_id
      try {
        const { data } = await api.get(`/cajas/sesiones/${sesionId}`)
        this.sesion      = data.sesion
        this.movimientos = data.sesion.movimientos || []
        this.resumen     = data.sesion.resumen     || this.resumen
      } catch {
        this.error = 'Error al cargar los movimientos de la sesión'
      } finally {
        this.cargando = false
      }
    },

    fmt(val) { return parseFloat(val || 0).toFixed(2) },

    formatearFecha(fecha) {
      if (!fecha) return '—'
      return new Date(fecha).toLocaleString('es-EC', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    },

    esManual(mov) {
      return ['transferencia', 'ajuste'].includes(mov.origen)
        && (mov.concepto || '') !== 'apertura'
    },

    abrirEditar(mov) {
      this.modalEditar = {
        visible: true, guardando: false, error: null,
        id: mov.id,
        monto: parseFloat(mov.monto || 0),
        descripcion: mov.descripcion || mov.concepto || '',
        esTransferencia: mov.origen === 'transferencia',
      }
    },

    async guardarEdicion() {
      this.modalEditar.guardando = true
      this.modalEditar.error     = null
      try {
        await api.put(`/cajas/movimientos/${this.modalEditar.id}`, {
          monto:       this.modalEditar.monto,
          descripcion: this.modalEditar.descripcion,
        })
        this.modalEditar.visible = false
        await this.cargar()
      } catch (e) {
        this.modalEditar.error = e?.response?.data?.error || 'Error al guardar'
      } finally {
        this.modalEditar.guardando = false
      }
    },

    async eliminar(mov) {
      const msg = mov.origen === 'transferencia'
        ? `¿Eliminar el depósito de $${this.fmt(mov.monto)}? También se eliminará el movimiento de banco asociado.`
        : `¿Eliminar el movimiento de $${this.fmt(mov.monto)}?`
      if (!confirm(msg)) return
      try {
        await api.delete(`/cajas/movimientos/${mov.id}`)
        await this.cargar()
      } catch (e) {
        alert(e?.response?.data?.error || 'Error al eliminar')
      }
    },
  },
}
</script>
